import React                from "react";
import Box                  from "@mui/material/Box";
import Avatar               from "@mui/material/Avatar";
import Button               from "@mui/material/Button";
import CircularProgress     from '@mui/material/CircularProgress';
import Typography           from "@mui/material/Typography";
import Select               from "@mui/material/Select";
import MenuItem             from '@mui/material/MenuItem';
import Check                from '@mui/icons-material/Check';
import { IProgramOption }   from "hooks/useProgramCatalog";
import { StudentDraft }     from "ui/components/classes-components/ClassFlow";
import PROFILE_ICON         from 'images/profile_icon.png';
import {
    REVIEW_STUDENTS_CONFIRM,
}                           from "config/config";

// One roster row — memoized so a level pick or dropdown open/close rerenders only the
// affected row(s), not rows × options (review #340 item 5). All callbacks take the row
// key so the parent can keep them referentially stable.
const StudentRow = React.memo(function StudentRow({
    rowKey,
    name,
    value,
    open,
    programs,
    onOpen,
    onClose,
    onChange,
}: {
    rowKey: number;
    name: string;
    value: string;
    open: boolean;
    programs: IProgramOption[];
    onOpen: (key: number) => void;
    onClose: () => void;
    onChange: (key: number, program: string) => void;
}) {
    return (
        <Box
            onClick={() => { if (!open) onOpen(rowKey); }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
                px: 1,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: open ? '#F1F1F1' : 'transparent',
                // Only on real hover devices — otherwise a tap latches :hover
                // and the row stays grey after the dropdown closes (touch).
                '@media (hover: hover)': { '&:hover': { bgcolor: '#F1F1F1' } },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Avatar src={PROFILE_ICON} alt={name} sx={{ width: 36, height: 36 }} />
                <Typography noWrap>{name}</Typography>
            </Box>
            <Select
                variant="standard"
                disableUnderline
                open={open}
                value={value}
                onChange={(e) => onChange(rowKey, e.target.value as string)}
                onOpen={() => onOpen(rowKey)}
                onClose={onClose}
                renderValue={(val) => programs.find((p) => p.id === val)?.label ?? ''}
                sx={{ flexShrink: 0, fontWeight: 400, '& .MuiSelect-select': { pr: 3 }, '& .MuiSelect-icon': { color: '#AAAAAA' } }}
            >
                {programs.map((p) => (
                    <MenuItem
                        key={p.id}
                        value={p.id}
                        sx={{
                            '&:hover': { backgroundColor: '#F1F1F1' },
                            '&.Mui-selected': { backgroundColor: 'transparent' },
                            '&.Mui-selected:hover': { backgroundColor: '#F1F1F1' },
                        }}
                    >
                        <Box component="span" sx={{ display: 'inline-flex', width: 28, color: 'primary.main' }}>
                            {value === p.id && <Check fontSize="small" />}
                        </Box>
                        {p.label}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
});

// Body of the "Review students" step: the roster the teacher just entered (client
// state — no refetch, the students don't exist server-side yet) with a per-student
// program-level picker defaulted from the class grade. Confirm hands the overrides to
// the host (ClassFlow), which sends the whole flow as one request.
export default function ReviewStudentsList({
    students,
    defaultProgram,
    programs,
    saving,
    error,
    onConfirm,
}: {
    students: StudentDraft[];
    defaultProgram?: string;
    programs: IProgramOption[];
    saving: boolean;
    error: string;
    onConfirm: (overrides: Record<number, string>) => void;
}) {
    // Per-student overrides keyed by draft key (only set when changed here).
    const [levels, setLevels] = React.useState<Record<number, string>>({});
    // Which student's level dropdown is open (drives the row highlight).
    const [openKey, setOpenKey] = React.useState<number | null>(null);

    const handleOpen = React.useCallback((key: number) => setOpenKey(key), []);
    const handleClose = React.useCallback(() => setOpenKey(null), []);
    const handleChange = React.useCallback((key: number, program: string) => {
        setLevels((prev) => ({ ...prev, [key]: program }));
    }, []);

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ width: '100%', maxHeight: 320, overflowY: 'auto' }}>
                {students.map((s) => (
                    <StudentRow
                        key={s.key}
                        rowKey={s.key}
                        name={s.name}
                        value={levels[s.key] ?? s.program ?? defaultProgram ?? ''}
                        open={openKey === s.key}
                        programs={programs}
                        onOpen={handleOpen}
                        onClose={handleClose}
                        onChange={handleChange}
                    />
                ))}
            </Box>
            <Button
                fullWidth
                variant="contained"
                onClick={() => onConfirm(levels)}
                disabled={saving}
                sx={{ boxShadow: 'none' }}
            >
                {REVIEW_STUDENTS_CONFIRM}
            </Button>
            {error &&
                <Typography align="center" color="error">{error}</Typography>
            }
            {saving &&
                <CircularProgress sx={{ alignSelf: 'center' }} />
            }
        </Box>
    );
}
